(() => {
  const sourceElement = document.getElementById('product-structured-data');
  if (!sourceElement) return;

  let source;
  try {
    source = JSON.parse(sourceElement.textContent);
  } catch {
    return;
  }
  if (!source || !['Product', 'ProductGroup'].includes(source['@type'])) return;

  const selector = 'script.y-rich-snippet-script[type="application/ld+json"]';
  const variants = [source['@type'] === 'ProductGroup' ? source.hasVariant || [] : source].flat().filter(variant => variant && typeof variant === 'object');
  const sourceOffers = variants.flatMap(variant => variant.offers || []).filter(offer => offer && typeof offer === 'object');
  const hasText = value => typeof value === 'string' && value.trim().length > 0;
  const hasBrand = value => hasText(value) || Boolean(value && ['Brand', 'Organization'].includes(value['@type']) && (hasText(value.name) || hasText(value['@id'])));
  const validAvailability = value => typeof value === 'string' && /^https?:\/\/schema\.org\/(InStock|OutOfStock|PreOrder|BackOrder|Discontinued|InStoreOnly|OnlineOnly|LimitedAvailability|SoldOut|PreSale)$/.test(value);
  const copy = value => JSON.parse(JSON.stringify(value));

  function sameProduct(entity) {
    if (entity['@type'] !== 'Product') return false;
    if (hasText(entity.url) && hasText(source.url)) {
      try {
        const actual = new URL(entity.url, location.href);
        const expected = new URL(source.url, location.href);
        return actual.origin === expected.origin && actual.pathname === expected.pathname;
      } catch {
        return false;
      }
    }
    return hasText(entity.name) && entity.name.trim() === source.name?.trim();
  }

  function sharedPolicy(key) {
    if (!sourceOffers.length || !sourceOffers[0][key]) return null;
    const first = JSON.stringify(sourceOffers[0][key]);
    return sourceOffers.every(offer => JSON.stringify(offer[key]) === first) ? sourceOffers[0][key] : null;
  }

  function exactVariant(entity) {
    if (variants.length === 1) return variants[0];
    if (hasText(entity.sku)) {
      const matches = variants.filter(variant => variant.sku === entity.sku);
      if (matches.length === 1) return matches[0];
    }
    const offers = Array.isArray(entity.offers) ? entity.offers : [entity.offers];
    if (offers.length !== 1 || !hasText(offers[0]?.url)) return null;
    let selectedId;
    try {
      selectedId = new URL(offers[0].url, location.href).searchParams.get('variant');
    } catch {
      return null;
    }
    if (!selectedId) return null;
    return variants.find(variant => [variant.offers].flat().some(offer => {
      if (!hasText(offer?.url)) return false;
      try {
        return new URL(offer.url, location.href).searchParams.get('variant') === selectedId;
      } catch {
        return false;
      }
    })) || null;
  }

  function validGtin(value) {
    if (typeof value !== 'string' || !/^(\d{8}|\d{12}|\d{13}|\d{14})$/.test(value) || /^0+$/.test(value)) return false;
    let sum = 0;
    for (let index = value.length - 2, weight = 3; index >= 0; index--, weight = weight === 3 ? 1 : 3) {
      sum += Number(value[index]) * weight;
    }
    return (10 - sum % 10) % 10 === Number(value[value.length - 1]);
  }

  const returnPolicy = sharedPolicy('hasMerchantReturnPolicy');
  const shippingPolicy = sharedPolicy('shippingDetails');

  function enrich(entity) {
    if (Array.isArray(entity)) return entity.reduce((changed, item) => enrich(item) || changed, false);
    if (!entity || typeof entity !== 'object') return false;
    let changed = entity['@graph'] ? enrich(entity['@graph']) : false;
    if (!sameProduct(entity)) return changed;

    if (!hasText(entity.description) && hasText(source.description)) {
      entity.description = source.description;
      changed = true;
    }
    if (!hasBrand(entity.brand) && hasBrand(source.brand)) {
      entity.brand = copy(source.brand);
      changed = true;
    }

    const gtinKeys = ['gtin', 'gtin8', 'gtin12', 'gtin13', 'gtin14'];
    if (!gtinKeys.some(key => hasText(entity[key]))) {
      const variant = exactVariant(entity);
      const key = variant && gtinKeys.find(key => validGtin(variant[key]));
      if (key) {
        entity[key] = variant[key];
        changed = true;
      }
    }

    const offers = Array.isArray(entity.offers) ? entity.offers : [entity.offers];
    const matchingVariant = exactVariant(entity);
    const matchingOffers = matchingVariant ? [matchingVariant.offers].flat().filter(Boolean) : [];
    for (const offer of offers) {
      if (!offer || offer['@type'] !== 'Offer') continue;
      if (offer.availability && !validAvailability(offer.availability) && matchingOffers.length === 1 && validAvailability(matchingOffers[0].availability)) {
        offer.availability = matchingOffers[0].availability;
        changed = true;
      }
      if (!offer.hasMerchantReturnPolicy && returnPolicy) {
        offer.hasMerchantReturnPolicy = copy(returnPolicy);
        changed = true;
      }
      if (!offer.shippingDetails && shippingPolicy && offer.priceCurrency === 'USD') {
        offer.shippingDetails = copy(shippingPolicy);
        changed = true;
      }
    }
    return changed;
  }

  function update(element) {
    if (!element?.matches?.(selector)) return;
    try {
      const data = JSON.parse(element.textContent);
      if (enrich(data)) element.textContent = JSON.stringify(data);
    } catch {
      // Yotpo can append the script before populating it. A later mutation retries it.
    }
  }

  // Yotpo may load after the product page or recreate its script when reviews refresh.
  const observer = new MutationObserver(records => {
    const pending = new Set();
    for (const record of records) {
      const target = record.target.nodeType === 1 ? record.target : record.target.parentElement;
      if (target?.matches?.(selector)) pending.add(target);
      for (const node of record.addedNodes || []) {
        if (node.matches?.(selector)) pending.add(node);
        node.querySelectorAll?.(selector).forEach(element => pending.add(element));
      }
    }
    pending.forEach(update);
  });
  observer.observe(document.documentElement, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['class', 'type'] });
  document.querySelectorAll(selector).forEach(update);
})();
